import { BasketPermissionOutputData } from './../../class/bax/basketPermissionOutputData';
import { BasketPermissionInputData } from '../../class/bax/basketPermissionInputData';
import { TokenStorage } from 'app/auth/token-storage.service';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, ReplaySubject, ConnectableObservable, Subscription } from 'rxjs';
import { AppConfigService } from 'app/utils/appconfig/app-config.service';
import { Injectable, OnDestroy } from '@angular/core';
import { AddBasketInsurance } from 'app/class/bax/addBasketInsurance';
import { BasketDetails } from 'app/class/bax/basketDetails';
import { BasketInsuranceDetails, DataCalculation, DataCommit } from 'app/class/bax/basketInsuranceDetails';
import { BasketInsuranceCalculationDetails } from 'app/class/bax/basketInsuranceCalculationDetails';
import { UpdateBasket } from 'app/class/bax/updateBasket';
import { CommitResult } from 'app/class/bax/commitResult';
import { CommitStatus } from 'app/class/bax/commitStatus';
import { BasketCustomerDetails } from 'app/class/bax/basketCustomerDetails';
import { CustomerAdditionalInfo, UpdateBasketCustomer } from 'app/class/bax/updateBasketCustomer';
import { SetAddressWhenAddressProtection } from 'app/class/bax/setAddressWhenAddressProtection';
import { UpdateBasketInsurance } from 'app/class/bax/updateBasketInsurance';
import { NemIdValidateRequest } from 'app/class/bax/nemIdValidateRequest';
import { NemIdSignValidateResponse } from 'app/class/bax/nemIdSignValidateResponse';
import { NemIdSignParameters } from 'app/class/bax/nemIdSignParameters';
import { Cookie } from 'ng2-cookies/ng2-cookies';
import { Router } from '@angular/router';
import { NavigationService } from '../navigation/navigation.service';
import { debounceTime, map, publish, switchMap, take, tap } from 'rxjs/operators';
import { AuthService } from 'app/auth/auth.service';
import { CustomerOfferService } from 'app/selfcare/tasks/customeroffer.service';
import { CampaignService } from '../campaign/campaign.service';
import { ObservableHelper } from '../observableHelper/observableHelper';

export interface IUpdateBasketInputs {
    basketId: string;
    insuranceId: string;
    data: UpdateBasketInsurance;
}

export interface ICalculateBasketInsuranceInputs {
    basketId: string;
    insuranceId: string;
}

@Injectable()
export class BaxService implements OnDestroy {
    public basketInsuranceCountSubject = new BehaviorSubject<number | null>(null);

    public prefilledCustomerPhoneNo: string;

    private apiUrl: string;
    private isHandlingError = false;
    private missingBasketIdMessage = 'Missing basket ID';
    private updateBasketSubject: ReplaySubject<IUpdateBasketInputs>;
    private updateBasketSubjectObservable: ConnectableObservable<BasketInsuranceDetails>;
    private calculateBasketInsuranceSubject: ReplaySubject<ICalculateBasketInsuranceInputs>;
    private calculateBasketInsuranceSubjectObservable: ConnectableObservable<BasketInsuranceCalculationDetails[]>;
    private subscriptions: Subscription[] = [];

    constructor(
        public http: HttpClient,
        private config: AppConfigService,
        private router: Router,
        private navigationService: NavigationService,
        private authService: AuthService,
        private customerOfferService: CustomerOfferService,
        private campaignService: CampaignService,
        private observableHelper: ObservableHelper,
        private tokenStorage: TokenStorage
    ) {
        this.apiUrl = this.config.appConfig.baxUrl;

        this.updateBasketSubject = new ReplaySubject<IUpdateBasketInputs>(1);
        // debounce 300ms to prevent multiple calls
        this.updateBasketSubjectObservable = publish<BasketInsuranceDetails>()(
            this.updateBasketSubject.asObservable().pipe(
                debounceTime(300),
                switchMap((inputs: IUpdateBasketInputs) => this._updateBasket(inputs))
            )
        );

        const updateBasketSubscription = this.updateBasketSubjectObservable.connect();
        this.subscriptions.push(updateBasketSubscription);

        this.calculateBasketInsuranceSubject = new ReplaySubject<ICalculateBasketInsuranceInputs>(1);
        // debounce 300ms to prevent multiple calls
        this.calculateBasketInsuranceSubjectObservable = publish<BasketInsuranceCalculationDetails[]>()(
            this.calculateBasketInsuranceSubject.asObservable().pipe(
                debounceTime(300),
                switchMap((inputs: ICalculateBasketInsuranceInputs) => this._calculateBasketInsurance(inputs))
            )
        );

        const calculateBasketInsuranceSubscription = this.calculateBasketInsuranceSubjectObservable.connect();
        this.subscriptions.push(calculateBasketInsuranceSubscription);
    }

    ngOnDestroy() {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    // Basket methods -->
    async basketCreate(): Promise<BasketDetails> {
        const url = `${this.apiUrl}/baskets`;
        let basketDetails: BasketDetails = null;
        const leadCampaignId = this.tokenStorage.getLeadCampaignId();
        if (leadCampaignId) {
            basketDetails = new BasketDetails();
            basketDetails.data = this.objectToString({ LeadCampaignId: leadCampaignId });
        }
        const basket = await this.http.post<BasketDetails>(url, basketDetails).toPromise();

        if (basket) {
            // Update basket customer if logged in
            if (this.authService.isAuthenticated()) {
                const updateCustomerData: UpdateBasketCustomer = new UpdateBasketCustomer();
                updateCustomerData.existingCustomerNumber = this.authService.getCustomerNo();
                await this.customerUpdate(basket.publicId, updateCustomerData).toPromise();
            }
        }

        this.basketInsuranceCountSubject.next(0);

        return basket;
    }

    basketGet(basketId: string): Observable<BasketDetails> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}`;
        const observable = this.http.get<BasketDetails>(url);
        return observable.pipe(tap({ error: this.onBasketError.bind(this) }));
    }

    // currently not in use
    basketUpdate(basketId: string, data: UpdateBasket): Observable<BasketDetails> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}`;
        const observable = this.http.put<BasketDetails>(url, data);
        return observable.pipe(tap({ error: this.onBasketError.bind(this) }));
    }

    basketCommit(basketId: string): Observable<CommitResult> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/commitbasket`;
        const observable = this.http.put<CommitResult>(url, null);
        return observable.pipe(
            tap({
                next: () => {
                    this.basketInsuranceCountSubject.next(0);
                },
                error: this.onBasketError.bind(this),
                complete: () => {
                    this.customerOfferService.invalidateCache();
                    this.campaignService.invalidateCache();
                },
            })
        );
    }

    basketGetCommitStatus(basketId: string): Observable<CommitStatus> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/commitbasket`;
        const observable = this.http.get<CommitStatus>(url);
        return observable.pipe(tap({ error: this.onBasketError.bind(this) }));
    }

    basketSendLinkEmail(basketId: string, sendToEmail: string): Observable<void> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/emailbasket/?email=${sendToEmail}`;
        const observable = this.http.put<void>(url, null);
        return observable.pipe(tap({ error: this.onBasketError.bind(this) }));
    }
    // Basket methods <--

    // BasketCustomer methods -->
    customerGet(basketId: string): Observable<BasketCustomerDetails> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/customer`;
        const observable = this.http.get<BasketCustomerDetails>(url);
        return observable.pipe(tap({ error: this.onBasketError.bind(this) }));
    }

    customerUpdate(basketId: string, data: UpdateBasketCustomer): Observable<BasketCustomerDetails> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/customer`;
        const inputData = {
            customer: data.customer,
            address: data.address,
            email: data.email,
            phoneNumber: data.phoneNumber,
            relation: data.relation,
            existingCustomerNumber: data.existingCustomerNumber,
            data: this.objectToString(data.data),
        };
        const observable = this.http.put<BasketCustomerDetails>(url, inputData);
        return observable.pipe(tap({ error: this.onBasketError.bind(this) }));
    }

    customerSetAddressByCpr(basketId: string): Observable<void> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/customer/setaddressbycpr`;
        const observable = this.http.put<void>(url, null);
        return observable.pipe(tap({ error: this.onBasketError.bind(this) }));
    }

    customerSetAddressWhenAddressProtection(basketId: string, data: SetAddressWhenAddressProtection): Observable<void> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/customer/setaddresswhenaddressprotection`;
        const observable = this.http.post<void>(url, data);
        return observable.pipe(tap({ error: this.onBasketError.bind(this) }));
    }
    // BasketCustomer methods <--

    // Insurance methods -->
    insuranceAdd(basketId: string, data: AddBasketInsurance): Observable<BasketInsuranceDetails> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/insurances`;
        const inputData = {
            product: data.product,
            dataCalculation: this.objectToString(data.dataCalculation),
            dataCommit: this.objectToString(data.dataCommit),
        };

        const observable = this.http.post<BasketInsuranceDetails>(url, inputData);
        return observable.pipe(
            map((x) => {
                const count = this.basketInsuranceCountSubject.value + 1;
                this.basketInsuranceCountSubject.next(count);

                const result: BasketInsuranceDetails = {
                    publicId: x.publicId,
                    basketPublicId: x.basketPublicId,
                    product: x.product,
                    dataCalculation: x.dataCalculation ? (JSON.parse(x.dataCalculation.toString()) as DataCalculation) : null,
                    dataCommit: x.dataCommit ? (JSON.parse(x.dataCommit.toString()) as DataCommit) : null,
                };
                return result;
            }),
            tap({ error: this.onBasketError.bind(this) })
        );
    }

    insurancesAddBulk(basketId: string, data: AddBasketInsurance[]): Observable<BasketInsuranceDetails[]> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/insurances/bulk`;

        const inputData: any[] = [];
        data.forEach((x) => {
            const item = {
                product: x.product,
                dataCalculation: this.objectToString(x.dataCalculation),
                dataCommit: this.objectToString(x.dataCommit),
            };
            inputData.push(item);
        });

        const observable = this.http.post<BasketInsuranceDetails[]>(url, inputData);
        return observable.pipe(
            tap({
                next: () => {
                    const count = this.basketInsuranceCountSubject.value + data.length;

                    this.basketInsuranceCountSubject.next(count);
                },
                error: this.onBasketError.bind(this),
            })
        );
    }

    insurancesGet(basketId: string, callId?: string): Observable<BasketInsuranceDetails[]> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        // console.log('bax service insurancesGet called with callId: ' + callId);  //Keep for later task for reducing number of calls
        const url = `${this.apiUrl}/baskets/${basketId}/insurances`;
        const observable = this.http.get<BasketInsuranceDetails[]>(url);
        return observable.pipe(
            map((basketInsuranceDetailsArray) => {
                this.basketInsuranceCountSubject.next(basketInsuranceDetailsArray.length);
                const result: BasketInsuranceDetails[] = [];
                basketInsuranceDetailsArray.forEach((x) => {
                    const item: BasketInsuranceDetails = {
                        publicId: x.publicId,
                        basketPublicId: x.basketPublicId,
                        product: x.product,
                        dataCalculation: x.dataCalculation ? (JSON.parse(x.dataCalculation.toString()) as DataCalculation) : null,
                        dataCommit: x.dataCommit ? (JSON.parse(x.dataCommit.toString()) as DataCommit) : null,
                    };
                    result.push(item);
                });
                return result;
            }),
            tap({ error: this.onBasketError.bind(this) })
        );
    }

    insuranceGet(basketId: string, insuranceId: string): Observable<BasketInsuranceDetails> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/insurances/${insuranceId}`;
        const observable = this.http.get<BasketInsuranceDetails>(url);
        return observable.pipe(
            map((x) => {
                const result: BasketInsuranceDetails = {
                    publicId: x.publicId,
                    basketPublicId: x.basketPublicId,
                    product: x.product,
                    dataCalculation: x.dataCalculation ? (JSON.parse(x.dataCalculation?.toString()) as DataCalculation) : null,
                    dataCommit: x.dataCommit ? (JSON.parse(x.dataCommit?.toString()) as DataCommit) : null,
                };
                return result;
            }),
            tap({ error: this.onBasketError.bind(this) })
        );
    }

    insuranceUpdate(basketId: string, insuranceId: string, data: UpdateBasketInsurance): Observable<BasketInsuranceDetails> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }

        const inputs: IUpdateBasketInputs = { basketId, insuranceId, data };

        this.updateBasketSubject.next(inputs);

        return this.updateBasketSubjectObservable.pipe(take(1));
    }

    insuranceDelete(basketId: string, insuranceId: string): Observable<void> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/insurances/${insuranceId}`;
        const observable = this.http.delete<void>(url);
        return observable.pipe(
            tap({
                next: () => {
                    const count = this.basketInsuranceCountSubject.value - 1;

                    this.basketInsuranceCountSubject.next(count);
                },
                error: this.onBasketError.bind(this),
            })
        );
    }

    insuranceCalculate(basketId: string, insuranceId: string): Observable<BasketInsuranceCalculationDetails[]> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }

        const inputs: ICalculateBasketInsuranceInputs = { basketId, insuranceId };

        this.calculateBasketInsuranceSubject.next(inputs);

        return this.calculateBasketInsuranceSubjectObservable.pipe(take(1));
    }

    // currently not in use
    insuranceGetCalculations(basketId: string, insuranceId: string): Observable<BasketInsuranceCalculationDetails[]> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/insurances/${insuranceId}/calculations`;
        const observable = this.http.post<BasketInsuranceCalculationDetails[]>(url, null);
        return observable.pipe(tap({ error: this.onBasketError.bind(this) }));
    }

    // currently not in use
    insuranceGetCalculation(basketId: string, insuranceId: string, calculationId: string): Observable<BasketInsuranceCalculationDetails[]> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/insurances/${insuranceId}/calculations/${calculationId}`;
        const observable = this.http.post<BasketInsuranceCalculationDetails[]>(url, null);
        return observable.pipe(tap({ error: this.onBasketError.bind(this) }));
    }

    insuranceCalculationSelect(basketId: string, insuranceId: string, calculationId: string): Observable<void> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/insurances/${insuranceId}/calculations/${calculationId}/select`;
        const observable = this.http.post<void>(url, null);
        return observable.pipe(tap({ error: this.onBasketError.bind(this) }));
    }

    insuranceCalculationUnselect(basketId: string, insuranceId: string, calculationId: string): Observable<void> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/insurances/${insuranceId}/calculations/${calculationId}/unselect`;
        const observable = this.http.post<void>(url, null);
        return observable.pipe(tap({ error: this.onBasketError.bind(this) }));
    }

    insuranceCalculationGetSelected(basketId: string, insuranceId: string): Observable<BasketInsuranceCalculationDetails> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/insurances/${insuranceId}/calculations/selected`;
        const observable = this.http.get<BasketInsuranceCalculationDetails>(url);
        return observable.pipe(tap({ error: this.onBasketError.bind(this) }));
    }
    // Insurance methods <--

    // BasketPermission methods -->
    basketSetPermission(basketId: string, data: BasketPermissionInputData): Observable<void> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/permissions`;
        const observable = this.http.put<void>(url, data);
        return observable.pipe(tap({ error: this.onBasketError.bind(this) }));
    }

    basketGetPermission(basketId: string): Observable<BasketPermissionOutputData> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/permissions`;
        const observable = this.http.get<BasketPermissionOutputData>(url);
        return observable.pipe(tap({ error: this.onBasketError.bind(this) }));
    }

    basketDeletePermission(basketId: string): Observable<void> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/permissions`;
        const observable = this.http.delete<void>(url);
        return observable.pipe(tap({ error: this.onBasketError.bind(this) }));
    }
    // BasketPermission methods <--

    // BasketSign methods -->
    basketSignValidateNemIdSigning(basketId: string, request: NemIdValidateRequest): Observable<NemIdSignValidateResponse> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/nemid`;
        // Note: 404 handled elsewhere
        return this.http.post<NemIdSignValidateResponse>(url, request);
    }

    basketSignGetNemIdSigningParameters(basketId: string): Observable<NemIdSignParameters> {
        if (!basketId) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingBasketIdMessage);
        }
        const url = `${this.apiUrl}/baskets/${basketId}/nemid`;
        const observable = this.http.get<NemIdSignParameters>(url);
        return observable.pipe(tap({ error: this.onBasketError.bind(this) }));
    }
    // BasketSign methods <--

    syncBasket(basketId: string, customerNo: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const getSubscription = this.customerGet(basketId).subscribe((customerResp) => {
                // If existing basket is not already an existing customer, we update the basket with the authed customer number
                if (!customerResp.existingCustomer) {
                    const updateBasketCustomer: UpdateBasketCustomer = {
                        customer: customerResp.customer,
                        address: customerResp.address,
                        email: customerResp.email,
                        phoneNumber: customerResp.phoneNumber,
                        existingCustomerNumber: customerNo,
                        data: customerResp.data as CustomerAdditionalInfo,
                        relation: customerResp.relation,
                    };
                    const updateSubscription = this.customerUpdate(basketId, updateBasketCustomer).subscribe(
                        () => resolve(),
                        () => reject()
                    );
                    this.subscriptions.push(updateSubscription);
                } else {
                    if (customerNo !== customerResp.existingCustomerNumber) {
                        Cookie.delete('basketid');
                    }

                    resolve();
                }
            });
            this.subscriptions.push(getSubscription);
        });
    }

    private onBasketError(error: any): void {
        if (!this.isHandlingError && error && error.status === 404) {
            this.isHandlingError = true;
            Cookie.delete('basketid', '/');
            const isSavedBasketLink = window.location.href.indexOf('/b/') !== -1;
            const isCalculateLink = window.location.href.indexOf('/beregn/') !== -1;

            if (isSavedBasketLink) {
                this.basketCreate().then((response: BasketDetails) => {
                    Cookie.set('basketid', response.publicId, null, '/');
                    this.router.navigate([this.navigationService.getUrl('newcustomer')], {
                        queryParams: {
                            showExpiredBasketModal: true,
                        },
                    });
                    this.isHandlingError = false;
                });
            } else if (isCalculateLink) {
                this.router.navigate([this.navigationService.getUrl('frontpage')]);
                this.isHandlingError = false;
            } else {
                this.isHandlingError = false;
                // Do not redirect - let the user go where he intended
            }
        }
    }

    private _updateBasket(inputs: IUpdateBasketInputs): Observable<BasketInsuranceDetails> {
        const url = `${this.apiUrl}/baskets/${inputs.basketId}/insurances/${inputs.insuranceId}`;
        const inputData = {
            dataCalculation: this.objectToString(inputs.data.dataCalculation),
            dataCommit: this.objectToString(inputs.data.dataCommit),
        };
        const observable = this.http.put<BasketInsuranceDetails>(url, inputData).pipe(tap({ error: this.onBasketError.bind(this) }));
        return observable;
    }

    private _calculateBasketInsurance(inputs: ICalculateBasketInsuranceInputs): Observable<BasketInsuranceCalculationDetails[]> {
        const url = `${this.apiUrl}/baskets/${inputs.basketId}/insurances/${inputs.insuranceId}/calculate`;
        const observable = this.http.post<BasketInsuranceCalculationDetails[]>(url, null).pipe(tap({ error: this.onBasketError.bind(this) }));
        return observable;
    }

    private objectToString(object: any): string {
        const result = JSON.stringify(object, (key, value) => {
            if (value !== null) {
                return value;
            }
        });
        return result;
    }
}
